import type { FC } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import type { LinkPickerPlugin, LinkPickerProps } from '@atlaskit/link-picker';
import { LazyLinkPicker } from '@atlaskit/link-picker/lazy';
import { IconButton } from '@atlaskit/button/new';
import AddIcon from '@atlaskit/icon/core/add';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup, { type TriggerProps } from '@atlaskit/popup';
import Tooltip from '@atlaskit/tooltip';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { AtlassianLinkPickerPlugin } from '@atlassian/link-picker-atlassian-plugin';

import { useSearchProvider } from '@confluence/editor-features/entry-points/useSearchProvider.ts';

type OnSubmitPayload = Parameters<NonNullable<LinkPickerProps['onSubmit']>>[0];

export type LinkSubmitMethod = 'manual' | 'suggestion' | 'search';

type LinkPickerWithPluginProps = Omit<LinkPickerProps, 'plugins' | 'onSubmit'> & {
	onSubmit?: LinkPickerProps['onSubmit'];
	/** If true, shows the picker immediately without requiring button click */
	showPicker?: boolean;
	/** If true, disables the button */
	disabled: boolean;
	/** Called when a link is selected/submitted in the picker */
	onLinkSubmit: (link: { url: string; title: string; inputMethod: LinkSubmitMethod }) => void;
	/** Existing links to check for duplicates */
	existingLinks: Array<{ url: string; title?: string }>;
};

const i18n = defineMessages({
	add: {
		id: 'space-overview.smart-space-overview.link-picker.add',
		defaultMessage: 'Add link',
		description: 'Accessible label for the add link button.',
	},
	disabledTooltip: {
		id: 'space-overview.smart-space-overview.link-picker.disabled-tooltip',
		defaultMessage: 'Max of 4 links. Remove one to add another.',
		description: 'Tooltip text shown when the add link button is disabled due to capacity limits.',
	},
});

const POPUP_OFFSET: [number, number] = [0, 8];

export const LinkPickerButton: FC<LinkPickerWithPluginProps> = ({
	showPicker = false,
	disabled = false,
	onLinkSubmit,
	existingLinks = [],
	...props
}) => {
	const { formatMessage } = useIntl();
	const [isPickerOpen, setIsPickerOpen] = useState(showPicker);
	const searchProvider = useSearchProvider();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const plugins: [LinkPickerPlugin] = useMemo(
		() => [
			new AtlassianLinkPickerPlugin({
				searchProvider,
				activityClientEndpoint: `${window.location.origin}/gateway/api/graphql`,
			}),
		],
		[searchProvider],
	);

	const handleAddClick = useCallback(() => {
		if (disabled) return;
		setIsPickerOpen(true);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'openLinkPicker',
				source: 'smartSpaceOverview',
				attributes: {
					moduleType: 'topDocs',
				},
			},
		}).fire();
	}, [createAnalyticsEvent, disabled]);

	const handlePickerClose = useCallback(() => {
		setIsPickerOpen(false);
		props.onCancel?.();
	}, [props]);

	const handleSubmit = useCallback(
		(payload: OnSubmitPayload) => {
			const isDuplicate = existingLinks.some((link) => link.url === payload.url);

			if (isDuplicate) {
				setIsPickerOpen(false);
				return;
			}

			let inputMethod: LinkSubmitMethod = 'manual';
			if (payload.meta.inputMethod === 'manual') {
				inputMethod = 'manual';
			} else if (payload.data?.prefetch === true) {
				inputMethod = 'suggestion';
			} else {
				inputMethod = 'search';
			}

			onLinkSubmit?.({
				url: payload.url,
				title: payload.title ?? payload.displayText ?? payload.url,
				inputMethod,
			});
			setIsPickerOpen(false);
			props.onSubmit?.(payload);
		},
		[existingLinks, onLinkSubmit, props],
	);

	const renderContent = useCallback(
		({ update }: { update: () => void }) => (
			<LazyLinkPicker
				{...props}
				plugins={plugins}
				onCancel={handlePickerClose}
				onSubmit={handleSubmit}
				onContentResize={update}
			/>
		),
		[props, plugins, handlePickerClose, handleSubmit],
	);

	const renderTrigger = useCallback(
		(triggerProps: TriggerProps) => {
			const button = (
				<IconButton
					{...triggerProps}
					icon={AddIcon}
					label={formatMessage(i18n.add)}
					appearance="subtle"
					spacing="compact"
					isDisabled={disabled}
					onClick={handleAddClick}
				/>
			);

			if (disabled && formatMessage(i18n.disabledTooltip)) {
				return <Tooltip content={formatMessage(i18n.disabledTooltip)}>{button}</Tooltip>;
			}

			return button;
		},
		[formatMessage, disabled, handleAddClick],
	);

	return (
		<Popup
			isOpen={isPickerOpen}
			onClose={handlePickerClose}
			content={renderContent}
			trigger={renderTrigger}
			placement="bottom-start"
			offset={POPUP_OFFSET}
			autoFocus={false}
		/>
	);
};
